/**
 * @sazeo/sdk — Projects hooks
 *
 * TanStack Query wrappers for the projects addon. All calls route through
 * the Edge Function at `/addons/projects/*` guarded by
 * requireAddonEnabled() and enforced by RLS via the caller's JWT.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { buildFunctionUrl } from './functionUrl';
import type { SupabaseClientLike } from './people';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Project {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  status: 'planning' | 'active' | 'on_hold' | 'completed' | 'cancelled';
  priority: 'low' | 'medium' | 'high' | 'critical';
  start_date: string | null;
  due_date: string | null;
  owner_id: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface ProjectTask {
  id: string;
  tenant_id: string;
  project_id: string;
  title: string;
  description: string | null;
  status: 'todo' | 'in_progress' | 'in_review' | 'done';
  assignee_id: string | null;
  due_date: string | null;
  sort_order: number;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface ProjectCreateInput {
  name: string;
  description?: string | null;
  status?: Project['status'];
  priority?: Project['priority'];
  start_date?: string | null;
  due_date?: string | null;
  owner_id?: string | null;
}

export interface ProjectUpdateInput {
  name?: string;
  description?: string | null;
  status?: Project['status'];
  priority?: Project['priority'];
  start_date?: string | null;
  due_date?: string | null;
  owner_id?: string | null;
}

export interface ProjectTaskCreateInput {
  project_id: string;
  title: string;
  description?: string | null;
  status?: ProjectTask['status'];
  assignee_id?: string | null;
  due_date?: string | null;
}

export interface ProjectTaskUpdateInput {
  title?: string;
  description?: string | null;
  status?: ProjectTask['status'];
  assignee_id?: string | null;
  due_date?: string | null;
  sort_order?: number;
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const projectKeys = {
  all: ['projects'] as const,
  list: (tenantId: string) => [...projectKeys.all, 'list', tenantId] as const,
  detail: (id: string) => [...projectKeys.all, 'detail', id] as const,
  tasks: (projectId: string) => [...projectKeys.all, 'tasks', projectId] as const,
} as const;

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

async function getAuthHeaders(supabase: SupabaseClientLike): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { authorization: `Bearer ${token}` } : {};
}

async function apiFetch<T>(
  supabase: SupabaseClientLike,
  path: string,
  init?: RequestInit,
): Promise<T> {
  const headers = await getAuthHeaders(supabase);
  const res = await fetch(buildFunctionUrl(path), {
    ...init,
    headers: { 'content-type': 'application/json', ...headers, ...init?.headers },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
      error?: { message?: string };
    };
    throw new Error(body.error?.message ?? res.statusText);
  }
  if (res.status === 204) return null as T;
  return res.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useProjects(supabase: SupabaseClientLike, tenantId: string) {
  return useQuery({
    queryKey: projectKeys.list(tenantId),
    queryFn: () => apiFetch<Project[]>(supabase, 'addons/projects/projects'),
    enabled: Boolean(tenantId),
  });
}

export function useProject(supabase: SupabaseClientLike, tenantId: string, projectId: string) {
  void tenantId;
  return useQuery({
    queryKey: projectKeys.detail(projectId),
    queryFn: () => apiFetch<Project>(supabase, `addons/projects/projects/${projectId}`),
    enabled: Boolean(projectId),
  });
}

export function useProjectTasks(supabase: SupabaseClientLike, tenantId: string, projectId: string) {
  void tenantId;
  return useQuery({
    queryKey: projectKeys.tasks(projectId),
    queryFn: () => apiFetch<ProjectTask[]>(supabase, `addons/projects/projects/${projectId}/tasks`),
    enabled: Boolean(projectId),
  });
}

export function useCreateProject(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ProjectCreateInput) =>
      apiFetch<Project>(supabase, 'addons/projects/projects', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: projectKeys.list(tenantId) });
    },
  });
}

export function useUpdateProject(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: ProjectUpdateInput }) =>
      apiFetch<Project>(supabase, `addons/projects/projects/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: projectKeys.detail(vars.id) });
      void qc.invalidateQueries({ queryKey: projectKeys.list(tenantId) });
    },
  });
}

export function useDeleteProject(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(supabase, `addons/projects/projects/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: (_, id) => {
      void qc.invalidateQueries({ queryKey: projectKeys.detail(id) });
      void qc.invalidateQueries({ queryKey: projectKeys.list(tenantId) });
    },
  });
}

export function useCreateProjectTask(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  void tenantId;
  return useMutation({
    mutationFn: ({ projectId, input }: { projectId: string; input: ProjectTaskCreateInput }) =>
      apiFetch<ProjectTask>(supabase, `addons/projects/projects/${projectId}/tasks`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: projectKeys.tasks(vars.projectId) });
    },
  });
}

export function useUpdateProjectTask(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  void tenantId;
  return useMutation({
    mutationFn: ({
      projectId,
      taskId,
      patch,
    }: {
      projectId: string;
      taskId: string;
      patch: ProjectTaskUpdateInput;
    }) =>
      apiFetch<ProjectTask>(supabase, `addons/projects/projects/${projectId}/tasks/${taskId}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: projectKeys.tasks(vars.projectId) });
    },
  });
}
